import { Col, DatePicker, Form, Input, Row, Tooltip } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../components/DraggableModal";
import { EntUser } from "../../../models/user";
import SelectRole from "../../../components/selects/SelectRole";
import InputPassword from "../../../components/inputs/inputPassword";
import SelectPositions from "../../../components/selects/SelectPositions";
import SelectDepartment from "../../../components/selects/SelectDepartment";
import SelectGovernment from "../../../components/selects/SelectGovernment";
import { EntRole } from "../../../models/role";
import dayjs from 'dayjs';
import { getCookie } from "typescript-cookie";

enum FormField {
	pin,
	secondName,
	name,
	patronymic,
	password,
	roles,
	email,
	phone,
	governmentId,
	departmentId,
	temporaryAccessUntilTime,
	positionId
}

interface IProps {
	show: boolean;
	user: EntUser;
	onHide: () => void;
	onSubmit: (pos: EntUser) => Promise<boolean>;
}

export const AddUserModal = (props: IProps) => {
	const { t } = useTranslation();
	const [form] = useForm();
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedGovernment, setSelectedGovernment] = useState<number | null>(null);
	const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null);
	const [selectedRoles, setSelectedRoles] = useState<EntRole[] | null>(null);
	const [currPassLen, setCurrPassLen] = useState<number | null>(null);

	useEffect(() => {
		if (selectedRoles && selectedRoles.length > 0) {
			const maxPasswordLength = Math.max(
				...selectedRoles
					.map((role) => role.passwordLength ?? 0)
			);
			setCurrPassLen(maxPasswordLength > 0 ? maxPasswordLength : null);
		} else {
			setCurrPassLen(null);
		}
	}, [selectedRoles]);

	useEffect(() => {
		if (props.show) {
			setSelectedGovernment(props.user.governmentId ?? null);
			setSelectedDepartment(props.user.departmentId ?? null);
			setFieldsFromEntity();
		} else {
			setSelectedGovernment(null);
			setSelectedDepartment(null);
		}
	}, [props.show]);


	const setFieldsFromEntity = (): void => {
		const roleIds = props.user.roles
			? props.user.roles.map(role => role.id) : [];

		form.setFields([
			{ name: FormField.secondName, value: props.user.secondName },
			{ name: FormField.patronymic, value: props.user.patronymic },
			{ name: FormField.name, value: props.user.name },
			{ name: FormField.password, value: props.user.password },
			{ name: FormField.pin, value: props.user.pin },
			{ name: FormField.email, value: props.user.email },
			{ name: FormField.phone, value: props.user.phone },
			{ name: FormField.positionId, value: props.user.position?.id },
			{ name: FormField.governmentId, value: props.user.position?.department?.government?.id },
			{ name: FormField.departmentId, value: props.user.position?.department?.id },
			{
				name: FormField.temporaryAccessUntilTime,
				value: props.user.temporaryAccessUntilTime === null ? undefined : dayjs(props.user.temporaryAccessUntilTime)
			},

			{ name: FormField.roles, value: roleIds },

		]);
	}

	const setEntityFromFields = (): EntUser => {
		let userEntity = _.cloneDeep(props.user);
		userEntity.secondName = form.getFieldValue(FormField.secondName);
		userEntity.patronymic = form.getFieldValue(FormField.patronymic);
		userEntity.name = form.getFieldValue(FormField.name);
		userEntity.password = form.getFieldValue(FormField.password);
		userEntity.pin = form.getFieldValue(FormField.pin);
		userEntity.email = form.getFieldValue(FormField.email);
		userEntity.phone = form.getFieldValue(FormField.phone);
		userEntity.positionId = form.getFieldValue(FormField.positionId);
		userEntity.departmentId = form.getFieldValue(FormField.departmentId);
		userEntity.governmentId = form.getFieldValue(FormField.governmentId);
		userEntity.temporaryAccessUntilTime = form.getFieldValue(FormField.temporaryAccessUntilTime);
		userEntity.roleIds = form.getFieldValue(FormField.roles) || [];
		return userEntity;
	}

	const handleSubmit = async () => {
		setLoading(true);
		try {
			await form.validateFields();
			const data = setEntityFromFields();
			const result = await props.onSubmit(data);
			if (result) props.onHide();
		} finally {
			setLoading(false);
		}
	}
    const emailText = getCookie('i18next') === 'ky' ? "Чыныгы электрондук почтаңызды киргизиңиз. Бул почтага колдонуучуну түзүү учурунда жана сырсөздү калыбына келтирүү учурунда бир жолу колдонулуучу пароль жөнөтүлөт." : "Введите актуальную электронную почту. На данную почту будет отправлен одноразовый пароль при создании пользователя и при сбросе пароля.";
    const pinText = getCookie('i18next') === 'ky' ? "Персоналдык идентификациялык номер (ПИН) системада авторизациялоо үчүн пайдаланылат." : "Персональный идентификационный номер (ПИН) используется для авторизации в системе.";
	return (
		<>
			<DraggableModal
				title={props.user.id === 0 ? t('user.add_users') : t('user.edit_users')}
				visible={props.show}
				onCancel={props.onHide}
				onSubmit={handleSubmit}
				submitLoading={loading}
				width={800}
			>
				<Form
					initialValues={{ remember: true }}
					autoComplete="off"
					layout="vertical"
					form={form}

				>
					<Row gutter={10}>

						<Col span={12}>
						  <Tooltip title={pinText} >
							<Form.Item
								label={t('user.pin')}
								name={FormField.pin}
								rules={[
									{ required: true, message: t("msg.fill_in_the_field") },
									{
										validator: (_, value) => {
											if (!value) {
												return Promise.resolve();
											}

											const pinPattern = /^(1|2)(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])(19\d{2}|20[0-2]\d)\d{5}$/;
											if (!pinPattern.test(value)) {
												return Promise.reject(new Error(t('msg.error_fill_pin')));
											}

											return Promise.resolve();
										},
									},
									{
										len: 14,
										message: t('msg.desc_fill_pin'),
									},
								]}
							>
								<Input minLength={14} maxLength={14} showCount autoComplete="off" />
							</Form.Item>
						  </Tooltip>
							<Form.Item
								label={t('user.surname')}
								name={FormField.secondName}
								rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
							>
								<Input autoComplete="off" maxLength={255} showCount/>
							</Form.Item>
							<Form.Item
								label={t('user.name_employee')}
								name={FormField.name}
								rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
							>
								<Input autoComplete="off" maxLength={255} showCount/>
							</Form.Item>
							<Form.Item
								label={t('user.last_name')}
								name={FormField.patronymic}
							>
								<Input autoComplete="off" maxLength={255} showCount/>
							</Form.Item>
							<Tooltip title={emailText} >
								<Form.Item
									label={t('user.email')}
									name={FormField.email}
									rules={[{ required: true, message: t("msg.fill_in_the_field") }]}

								>
									<Input type="email" autoComplete="off" />
								</Form.Item>
							</Tooltip>
							<Form.Item
								label={t('user.phone_number')}
								name={FormField.phone}
								rules={[
									{
										required: true,
										message: t("msg.fill_in_the_field")
									},
									{
										pattern: /^996\d{9}$/,
										message: t('user.phone_number_desc')
									},
								]}
							>
								<Input count={{
									show: true,
									max: 12,
								}} maxLength={12} placeholder="996777000000" type="number" autoComplete="off" />
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label={t('user.organization')}
								name={FormField.governmentId}
								rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
							>
								<SelectGovernment
									value={selectedGovernment}
									onChange={(id) => {
										setSelectedGovernment(id);
										setSelectedDepartment(null);
										form.setFields([{ name: FormField.departmentId, value: null }, { name: FormField.positionId, value: null }, { name: FormField.roles, value: null }])
									}}
								/>
							</Form.Item>
							{
								props.user.position !== null || selectedGovernment !== null ?
									<Form.Item
										label={t('Отдел')}
										name={FormField.departmentId}
										rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
									>
										<SelectDepartment
											governmentIds={selectedGovernment !== null ? [selectedGovernment] : null}

											onChange={(id) => {
												setSelectedDepartment(id);
												form.setFields([{ name: FormField.positionId, value: null }, { name: FormField.roles, value: null }])
											}}
										/>
									</Form.Item>
									: ""
							}

							{
								props.user.position !== null || selectedGovernment !== null ?
									<Form.Item
										label={t('user.position')}
										name={FormField.positionId}
										rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
									>
										<SelectPositions
											onChange={() => {
												form.setFields([{ name: FormField.roles, value: null }])
											}}
											departmentIds={selectedDepartment !== null ? [selectedDepartment] : null}
										/>
									</Form.Item>
									: ""
							}

							<Form.Item
								label={t('role.role')}
								name={FormField.roles}
								rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
							>
								<SelectRole
									governmentIds={selectedGovernment !== null ? [selectedGovernment] : null}
									value={props.user.roles ? props.user.roles.map(role => role.id) : []}
									onChange={(id, options) => setSelectedRoles(options)}
								/>
							</Form.Item>

							{props.user.id !== 0 ? null : (
								<Form.Item
									label={t('auth.password')}
									name={FormField.password}
									rules={[
										{ required: true, message: t("msg.fill_in_the_field") },
										{
											pattern: new RegExp(/^.*(?=.{8,})(?=.*[a-zA-Z])(?=.*\d)(?=.*[!#$%&? ".]).*$/, 'g'),
											message: t('auth.passwordValidationErrorMessage')
										}
									]}
									hasFeedback
								>

									<InputPassword passwordLength={currPassLen} />
								</Form.Item>
							)}
							<Form.Item
								label={t('Доступ до ')}
								name={FormField.temporaryAccessUntilTime}
							>
								<DatePicker showTime placeholder={t('general.select_date')} />
							</Form.Item>
						</Col>
					</Row>


				</Form>
			</DraggableModal>
		</>
	)
}
